"use client";

import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { useTransition } from "react";
import { routing, type Locale } from "@/i18n/routing";

interface Props {
  /**
   * Visual variant.
   * - `"dark"`  — for dark topbar (default).
   * - `"light"` — for public nav on light backgrounds.
   */
  variant?: "dark" | "light";
}

/**
 * Locale switcher pill — client component.
 *
 * Wraps a hidden `<select>` styled as a label. Switching uses a hard
 * navigation (`window.location.href = target`) instead of `router.replace`
 * to work around a next-intl bug under `localePrefix: "as-needed"`:
 *
 *   When navigating from `/en/foo` back to `/foo` (default `es`, prefix
 *   stripped), the router does not detect the change and the page stays
 *   in English. A full reload guarantees both the URL update and an
 *   SSR re-evaluation of the messages bundle.
 */
export default function LanguageSwitcher({ variant = "dark" }: Props) {
  const t = useTranslations("languageSwitcher");
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const handleChange = (next: Locale) => {
    if (next === locale) return;
    const search = window.location.search;
    const hash = window.location.hash;
    const basePath = pathname === "/" ? "" : pathname;
    const target =
      next === routing.defaultLocale
        ? `${basePath || "/"}${search}${hash}`
        : `/${next}${basePath}${search}${hash}`;
    startTransition(() => {
      window.location.href = target;
    });
  };

  const baseClasses =
    variant === "dark"
      ? "bg-white/5 hover:bg-white/10 text-white/70"
      : "bg-dark/5 hover:bg-dark/10 text-dark/70";

  // Compact chip: shows just the ISO 639-1 code (ES / EN) so the
  // pill fits cleanly next to the rest of the navbar controls. The
  // <select> stays in the DOM for the actual interaction (full
  // language names visible in the native dropdown) and is overlaid
  // invisibly on top of the chip so clicks pass through to it.
  return (
    <label
      className={`relative inline-flex items-center justify-center rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors ${baseClasses} ${
        isPending ? "opacity-60" : ""
      }`}
      title={t("label")}
    >
      <span>{locale.toUpperCase()}</span>
      <select
        value={locale}
        onChange={(e) => handleChange(e.target.value as Locale)}
        disabled={isPending}
        className="absolute inset-0 opacity-0 cursor-pointer"
        aria-label={t("label")}
      >
        {routing.locales.map((loc) => (
          <option key={loc} value={loc} className="text-dark">
            {t(loc as "es" | "en")}
          </option>
        ))}
      </select>
    </label>
  );
}
